/**
 * Writing a letter: the draft, the send and the code-name re-roll.
 *
 * A letter is built, not written (Q10.1): the body is ids, the attachments are
 * ids and tile data, and there is no field anywhere a player can type into.
 * Attachments are normalised so nothing derived from the sender's account
 * travels with one (`docs/anonymity.md` R8).
 */

import { z } from 'zod'
import { letterBodySchema } from '../letter/grammar.ts'
import { mapSliceSchema } from '../letter/slice.ts'
import { MOODS } from '../content/schemas.ts'
import { gameDaySchema, positionSchema } from './common.ts'

/**
 * Up to one of each (Q4.6). The relic carries the coordinates the sender
 * *typed*, not the relic's real position and not whether they staked it; the
 * fragment carries only its id.
 */
export const attachmentsSchema = z.object({
  slice: mapSliceSchema.optional(),
  relic: z.object({ relicId: z.string(), at: positionSchema }).optional(),
  fragment: z.object({ fragmentId: z.string() }).optional(),
})
export type ApiAttachments = z.infer<typeof attachmentsSchema>

export const draftSchema = z.object({
  body: letterBodySchema,
  attachments: attachmentsSchema,
  /** The island the letter is about. */
  dayAbout: gameDaySchema,
  /** The code name it will be signed with, by id, and its re-roll bucket. */
  codeNameId: z.string(),
  moodBucket: z.enum(MOODS),
})
export type ApiDraft = z.infer<typeof draftSchema>

export const draftResponseSchema = z.object({ draft: draftSchema.nullable() })

/**
 * Saving a draft.
 *
 * `dayAbout` is the island the client cut this slice from, and it is **not**
 * optional (Q17.1). The clock is the wrong place to get it: a player who
 * writes about island D in the UTC evening and saves or sends after midnight
 * would otherwise have their letter relabelled as being about D+1, charted
 * onto the wrong ground, contradicted, and charged 0.10 for a lie they did not
 * tell. The client is the side that knows which island the slice came from, so
 * the client says, and the server refuses anything but today.
 */
export const saveDraftRequestSchema = z.object({
  body: letterBodySchema,
  attachments: attachmentsSchema,
  dayAbout: gameDaySchema,
})

/**
 * Sending carries the island the client believes its draft is about, and
 * nothing else: the body and the attachments were frozen when they were saved.
 * The server checks the claim against the stored draft rather than taking it,
 * so a wrong answer is a refusal and never a relabelling.
 */
export const sendRequestSchema = z.object({ dayAbout: gameDaySchema })

/**
 * What a send confirms. The code name is frozen into the letter; nothing here
 * says who it will reach, because that is decided in the delivery job, not at
 * send time (Q4.2).
 */
export const sendResponseSchema = z.object({
  codeNameId: z.string(),
  dayAbout: gameDaySchema,
  /** The day it becomes deliverable: tomorrow. */
  dayDeliverable: gameDaySchema,
})

/** A re-roll draws again inside the same mood bucket (Q4.5). */
export const rerollResponseSchema = z.object({
  codeNameId: z.string(),
  moodBucket: z.enum(MOODS),
})
